<template>
  <div class="bg-wrap">
    <p class="bg-intro">
      Personalizează pagina de login pentru fiecare client, după subdomeniu (ex.
      <strong>demo</strong> → demo.era-erp.ro, <strong>portalhr</strong> → portalhr.era-erp.ro).
      Modificările apar imediat la următoarea încărcare a paginii de login.
    </p>

    <div class="bg-layout">
      <!-- Lista clienti -->
      <aside class="bg-list">
        <div class="bg-list-head">
          <span>Clienți</span>
          <v-btn size="small" color="#ff2c21" variant="flat" @click="addNew">
            <v-icon start size="16">mdi-plus</v-icon> Adaugă
          </v-btn>
        </div>
        <div v-if="loading" class="bg-muted">Se încarcă...</div>
        <button v-for="b in items" :key="b.slug" class="bg-list-item"
          :class="{ active: selected && selected.slug === b.slug }" @click="select(b)">
          <span class="bg-li-slug">{{ b.slug }}</span>
          <span class="bg-li-badge" :class="{ off: !b.active }">{{ b.active ? 'activ' : 'inactiv' }}</span>
        </button>
        <div v-if="!loading && !items.length" class="bg-muted">Niciun client configurat.</div>
      </aside>

      <!-- Editor -->
      <section v-if="selected" class="bg-editor">
        <div class="bg-row">
          <div class="bg-field">
            <label>Slug (subdomeniu) *</label>
            <v-text-field v-model="selected.slug" :disabled="!isNew" density="compact"
              variant="outlined" hide-details placeholder="ex: demo" />
          </div>
          <div class="bg-field bg-color">
            <label>Culoare accent</label>
            <v-text-field v-model="selected.color" density="compact" variant="outlined"
              hide-details placeholder="red darken-1 sau #ff2c21" />
          </div>
          <div class="bg-field bg-active">
            <label>Activ</label>
            <v-switch v-model="selected.active" color="#ff2c21" hide-details density="compact" />
          </div>
        </div>

        <div class="bg-row">
          <div class="bg-field">
            <label>Logo (text)</label>
            <v-text-field v-model="selected.logoText" density="compact" variant="outlined"
              hide-details placeholder="ERA" />
          </div>
          <div class="bg-field">
            <label>Logo (URL imagine, opțional)</label>
            <v-text-field v-model="selected.logoUrl" density="compact" variant="outlined"
              hide-details placeholder="https://..." />
          </div>
        </div>

        <div class="bg-field">
          <label>Titlu</label>
          <v-text-field v-model="selected.title" density="compact" variant="outlined"
            hide-details placeholder="Bine ați venit" />
        </div>
        <div class="bg-field">
          <label>Subtitlu</label>
          <v-text-field v-model="selected.subtitle" density="compact" variant="outlined"
            hide-details placeholder="Sistem ERP..." />
        </div>

        <!-- Features -->
        <div class="bg-feat-head">
          <span>Module / features afișate</span>
          <v-btn size="x-small" variant="tonal" @click="addFeature">
            <v-icon start size="14">mdi-plus</v-icon> Adaugă rând
          </v-btn>
        </div>
        <div v-for="(f, i) in selected.features" :key="i" class="bg-feat-row">
          <v-text-field v-model="f.icon" density="compact" variant="outlined" hide-details
            placeholder="mdi-shield-check" style="max-width: 190px" />
          <v-text-field v-model="f.color" density="compact" variant="outlined" hide-details
            placeholder="#4CAF50" style="max-width: 110px" />
          <v-text-field v-model="f.title" density="compact" variant="outlined" hide-details
            placeholder="Titlu" />
          <v-text-field v-model="f.desc" density="compact" variant="outlined" hide-details
            placeholder="Descriere" />
          <v-btn icon size="x-small" variant="text" color="#ef4444" @click="selected.features.splice(i, 1)">
            <v-icon size="18">mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <div v-if="!selected.features.length" class="bg-muted">Niciun feature. Se afișează doar titlul.</div>

        <!-- Preview live -->
        <div class="bg-prev-head">Previzualizare pagină login</div>
        <div class="bg-preview">
          <div class="bgp-orbs"><span></span><span></span><span></span></div>
          <div class="bgp-content">
            <div class="bgp-logo">
              <img v-if="selected.logoUrl" :src="selected.logoUrl" alt="logo" class="bgp-logo-img" />
              <span v-else class="bgp-logo-text">{{ selected.logoText || 'ERA' }}</span>
              <span class="bgp-logo-sub">Enterprise Resource Application</span>
            </div>
            <h2 class="bgp-title">{{ selected.title || 'Bine ați venit' }}</h2>
            <p class="bgp-subtitle">{{ selected.subtitle || 'Sistem ERP' }}</p>
            <div class="bgp-features">
              <div v-for="(f, i) in selected.features" :key="i" class="bgp-feat">
                <span class="bgp-feat-ico" :style="{ background: hexAlpha(f.color) }">
                  <v-icon :color="f.color || '#fff'" size="20">{{ f.icon || 'mdi-star' }}</v-icon>
                </span>
                <div class="bgp-feat-txt">
                  <span class="bgp-feat-title">{{ f.title || 'Titlu' }}</span>
                  <span class="bgp-feat-desc">{{ f.desc }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-actions">
          <v-btn color="#ff2c21" :loading="saving" @click="save">
            <v-icon start>mdi-content-save</v-icon> Salvează
          </v-btn>
          <v-btn v-if="!isNew" variant="tonal" color="#ef4444" :loading="deleting" @click="remove">
            <v-icon start>mdi-delete</v-icon> Șterge
          </v-btn>
        </div>
      </section>

      <section v-else class="bg-editor bg-empty">
        <v-icon size="48" color="#cbd5e1">mdi-palette-outline</v-icon>
        <p>Selectează un client sau adaugă unul nou.</p>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import notify from "devextreme/ui/notify";
import BrandingAdminService from "@/services/AUTH/BrandingAdmin";
import type { Branding } from "@/services/BrandingService";

export default defineComponent({
  name: "BrandingGrid",
  data() {
    return {
      items: [] as Branding[],
      selected: null as Branding | null,
      isNew: false,
      loading: false,
      saving: false,
      deleting: false,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.items = await BrandingAdminService.getAll();
      } catch {
        notify("Nu am putut încărca branding-urile.", "error", 3000);
      } finally {
        this.loading = false;
      }
    },
    select(b: Branding) {
      this.isNew = false;
      this.selected = JSON.parse(JSON.stringify(b)); // copie editabilă
      if (!this.selected!.features) this.selected!.features = [];
    },
    addNew() {
      this.isNew = true;
      this.selected = { slug: "", color: "red darken-1", logoText: "ERA",
        title: "", subtitle: "", active: true, features: [] };
    },
    hexAlpha(hex?: string): string {
      const h = (hex || "").trim();
      if (/^#([0-9a-f]{6})$/i.test(h)) return h + "26"; // ~15% opacitate
      return "rgba(255,255,255,0.15)";
    },
    addFeature() {
      this.selected!.features.push({ icon: "mdi-star", color: "#2196F3", title: "", desc: "" });
    },
    async save() {
      if (!this.selected) return;
      if (!this.selected.slug || !this.selected.slug.trim()) {
        notify("Slug obligatoriu.", "warning", 2500); return;
      }
      this.selected.slug = this.selected.slug.trim().toLowerCase();
      this.saving = true;
      try {
        await BrandingAdminService.upsert(this.selected);
        notify("Salvat.", "success", 2000);
        this.isNew = false;
        await this.load();
      } catch (e: any) {
        notify(e?.response?.data?.Message || e?.response?.data || "Eroare la salvare.", "error", 3500);
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      if (!this.selected || this.isNew) return;
      if (!confirm(`Ștergi branding-ul pentru „${this.selected.slug}”?`)) return;
      this.deleting = true;
      try {
        await BrandingAdminService.remove(this.selected.slug);
        notify("Șters.", "success", 2000);
        this.selected = null;
        await this.load();
      } catch {
        notify("Eroare la ștergere.", "error", 3000);
      } finally {
        this.deleting = false;
      }
    },
  },
});
</script>

<style scoped>
.bg-wrap { display: flex; flex-direction: column; height: 100%; }
.bg-intro { color: #64748b; font-size: 13px; margin-bottom: 14px; }
.bg-layout { display: flex; gap: 16px; flex: 1; min-height: 0; }
.bg-list { width: 240px; flex-shrink: 0; border: 1px solid #e5e7eb; border-radius: 12px; overflow: auto; padding: 8px; }
.bg-list-head { display: flex; align-items: center; justify-content: space-between; font-weight: 700; font-size: 13px; color: #334155; padding: 4px 6px 10px; }
.bg-list-item { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; border: 1px solid transparent; border-radius: 9px; background: #f8fafc; margin-bottom: 6px; cursor: pointer; }
.bg-list-item.active { border-color: #ff2c21; background: #fff5f4; }
.bg-li-slug { font-weight: 600; color: #1e293b; font-size: 14px; }
.bg-li-badge { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #065f46; background: #ecfdf5; padding: 2px 8px; border-radius: 20px; }
.bg-li-badge.off { color: #92400e; background: #fef3c7; }
.bg-editor { flex: 1; min-width: 0; overflow: auto; border: 1px solid #e5e7eb; border-radius: 12px; padding: 18px; }
.bg-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; color: #94a3b8; gap: 10px; }
.bg-row { display: flex; gap: 12px; }
.bg-field { flex: 1; display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
.bg-field label { font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; }
.bg-color { max-width: 260px; }
.bg-active { max-width: 90px; }
.bg-feat-head { display: flex; align-items: center; justify-content: space-between; font-weight: 700; font-size: 13px; color: #334155; margin: 14px 0 8px; padding-top: 12px; border-top: 1px solid #f1f5f9; }
.bg-feat-row { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
.bg-muted { color: #94a3b8; font-size: 13px; padding: 8px 4px; }
.bg-actions { display: flex; gap: 10px; margin-top: 18px; padding-top: 14px; border-top: 1px solid #f1f5f9; }

/* Preview */
.bg-prev-head { font-weight: 700; font-size: 13px; color: #334155; margin: 18px 0 8px; padding-top: 12px; border-top: 1px solid #f1f5f9; }
.bg-preview { position: relative; overflow: hidden; border-radius: 14px; padding: 28px 30px;
  background: linear-gradient(135deg, #ff2c21 0%, #ff6a4a 60%, #ff8a5c 100%); color: #fff; }
.bgp-orbs span { position: absolute; border-radius: 50%; filter: blur(40px); opacity: 0.35; }
.bgp-orbs span:nth-child(1) { width: 180px; height: 180px; background: #fff; top: -60px; right: -40px; }
.bgp-orbs span:nth-child(2) { width: 140px; height: 140px; background: #ffd6c9; bottom: -50px; left: 20%; opacity: 0.25; }
.bgp-orbs span:nth-child(3) { width: 120px; height: 120px; background: #fff; bottom: 10px; right: 15%; opacity: 0.15; }
.bgp-content { position: relative; z-index: 1; }
.bgp-logo { display: flex; flex-direction: column; margin-bottom: 14px; }
.bgp-logo-text { font-size: 40px; font-weight: 800; letter-spacing: 6px; line-height: 1; }
.bgp-logo-img { max-height: 52px; align-self: flex-start; }
.bgp-logo-sub { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; opacity: 0.85; margin-top: 4px; }
.bgp-title { font-size: 26px; font-weight: 800; margin: 6px 0 2px; }
.bgp-subtitle { font-size: 13px; opacity: 0.9; margin-bottom: 18px; }
.bgp-features { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.bgp-feat { display: flex; gap: 10px; align-items: flex-start; background: rgba(255,255,255,0.12); border-radius: 12px; padding: 12px; }
.bgp-feat-ico { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.bgp-feat-txt { display: flex; flex-direction: column; min-width: 0; }
.bgp-feat-title { font-weight: 700; font-size: 13px; }
.bgp-feat-desc { font-size: 11px; opacity: 0.85; }
</style>
